import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { CustomField } from '@/lib/contacts/types'
import { AnyOfConditionRow } from '@/pages/SegmentBuiltInConditionRows'
import type { ConditionField, SegmentCondition } from '@/pages/SegmentRuleFields'

interface CustomFieldRowProps {
  field: CustomField
  condition: SegmentCondition
  onChange: (condition: SegmentCondition) => void
  onRemove: () => void
}

export function CustomFieldConditionRow(props: CustomFieldRowProps) {
  if (choiceCustomField(props.field)) {
    return (
      <AnyOfConditionRow
        label={props.field.label}
        condition={props.condition}
        options={customFieldChoices(props.field).map((value) => ({ value, label: value }))}
        onChange={props.onChange}
        onRemove={props.onRemove}
      />
    )
  }
  if (rangeCustomField(props.field)) return <CustomFieldRangeRow {...props} />

  return <CustomFieldTextRow {...props} />
}

function CustomFieldTextRow({ field, condition, onChange, onRemove }: CustomFieldRowProps) {
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-input px-3 py-2">
      <span className="text-body-sm">{field.label}</span>
      <Select
        value={condition.operator}
        onValueChange={(operator) => onChange({ ...condition, operator })}
      >
        <SelectTrigger aria-label={`${field.label} comparison`} className="h-8 w-28">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="equals">equals</SelectItem>
          <SelectItem value="contains">contains</SelectItem>
        </SelectContent>
      </Select>
      <Input
        aria-label={`${field.label} value`}
        className="h-8 w-44"
        value={String(condition.values[0] ?? '')}
        onChange={(event) => onChange({ ...condition, values: [event.target.value] })}
      />
      <RemoveButton onClick={onRemove} />
    </div>
  )
}

function CustomFieldRangeRow({ field, condition, onChange, onRemove }: CustomFieldRowProps) {
  const inputType = field.field_type === 'date' ? 'date' : 'text'
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-input px-3 py-2">
      <span className="text-body-sm">{field.label} between</span>
      <Input
        type={inputType}
        inputMode={field.field_type === 'number' ? 'decimal' : undefined}
        aria-label={`${field.label} from`}
        className="h-8 w-32"
        value={String(condition.values[0] ?? '')}
        onChange={(event) =>
          onChange({ ...condition, values: [event.target.value, condition.values[1] ?? ''] })
        }
      />
      <span className="text-body-sm text-muted-foreground">and</span>
      <Input
        type={inputType}
        inputMode={field.field_type === 'number' ? 'decimal' : undefined}
        aria-label={`${field.label} to`}
        className="h-8 w-32"
        value={String(condition.values[1] ?? '')}
        onChange={(event) =>
          onChange({ ...condition, values: [condition.values[0] ?? '', event.target.value] })
        }
      />
      <RemoveButton onClick={onRemove} />
    </div>
  )
}

function RemoveButton({ onClick }: { onClick: () => void }) {
  return (
    <Button type="button" variant="outline" onClick={onClick}>
      Remove
    </Button>
  )
}

export function customConditionField(field: CustomField): ConditionField {
  return `custom_field:${field.id}`
}

export function defaultCustomFieldCondition(field: CustomField): SegmentCondition {
  const conditionField = customConditionField(field)
  if (choiceCustomField(field)) {
    const first = customFieldChoices(field)[0]
    return { field: conditionField, operator: 'any_of', values: first ? [first] : [] }
  }
  if (rangeCustomField(field)) {
    return { field: conditionField, operator: 'between', values: ['', ''] }
  }
  return { field: conditionField, operator: 'equals', values: [''] }
}

function choiceCustomField(field: CustomField) {
  return ['single-select', 'multi-select', 'checkbox'].includes(field.field_type)
}

function rangeCustomField(field: CustomField) {
  return field.field_type === 'number' || field.field_type === 'date'
}

function customFieldChoices(field: CustomField) {
  return field.field_type === 'checkbox' ? ['true', 'false'] : field.choices
}
